import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const scrollToBooking = () => {
    document.getElementById('book')?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isYoungDriversPage = location.pathname === '/young-drivers';
  const isDriveWithConfidencePage = location.pathname === '/drive-with-confidence';
  const showServiceNav = isYoungDriversPage || isDriveWithConfidencePage;

  return (
    <header className="sticky top-0 z-50 bg-[#0a1628]/95 backdrop-blur-md shadow-lg">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex justify-between items-center py-3">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src="/kyle-drive-logo.png"
              alt="Kyle Drive"
              className="h-16 sm:h-20 lg:h-32 w-auto cursor-pointer animate-pulse hover:animate-none"
              onClick={handleLogoClick}
              style={{ animationDuration: '3s' }}
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {showServiceNav ? (
              <>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-white hover:text-cyan-400 font-medium transition-colors"
                >
                  About
                </button>
                <button
                  onClick={() => scrollToSection('facilities')}
                  className="text-white hover:text-cyan-400 font-medium transition-colors"
                >
                  Facilities
                </button>
                <button
                  onClick={() => scrollToSection('faq')}
                  className="text-white hover:text-cyan-400 font-medium transition-colors"
                >
                  FAQs
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-white hover:text-cyan-400 font-medium transition-colors"
                >
                  Contact
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/young-drivers')}
                  className="text-white hover:text-cyan-400 font-medium transition-colors"
                >
                  Young Drivers
                </button>
                <button
                  onClick={() => navigate('/drive-with-confidence')}
                  className="text-white hover:text-cyan-400 font-medium transition-colors"
                >
                  Drive With Confidence
                </button>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-white hover:text-cyan-400 font-medium transition-colors"
                >
                  About
                </button>
                <button
                  onClick={scrollToBooking}
                  className="text-white hover:text-cyan-400 font-medium transition-colors"
                >
                  Book Now
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-white hover:text-cyan-400 font-medium transition-colors"
                >
                  Contact
                </button>
              </>
            )}
            
            {showServiceNav && (
              <button
                onClick={scrollToBooking}
                className="ml-4 px-5 py-2 bg-gradient-to-r from-[#0ea5e9] to-[#06b6d4] hover:from-[#0284c7] hover:to-[#0891b2] text-white rounded-lg font-bold shadow-lg hover:shadow-xl transition-all text-sm"
              >
                <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Book Now
              </button>
            )}
          </nav>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 rounded-md text-white hover:bg-white/10"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 space-y-2 border-t border-gray-200">
            {showServiceNav ? (
              <>
                <button
                  onClick={() => scrollToSection('about')}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded"
                >
                  About
                </button>
                <button
                  onClick={() => scrollToSection('facilities')}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded"
                >
                  Facilities
                </button>
                <button
                  onClick={() => scrollToSection('faq')}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded"
                >
                  FAQs
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded"
                >
                  Contact
                </button>
                <button
                  onClick={scrollToBooking}
                  className="block w-full text-left px-4 py-2 bg-gradient-to-r from-blue-600 to-teal-500 text-white rounded-lg font-semibold mt-2"
                >
                  Book Now
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { navigate('/young-drivers'); setIsMenuOpen(false); }}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded"
                >
                  Young Drivers
                </button>
                <button
                  onClick={() => { navigate('/drive-with-confidence'); setIsMenuOpen(false); }}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded"
                >
                  Drive With Confidence
                </button>
                <button
                  onClick={() => scrollToSection('about')}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded"
                >
                  About
                </button>
                <button
                  onClick={scrollToBooking}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded"
                >
                  Book Now
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded"
                >
                  Contact
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
